import * as THREE from 'three'

import { getObjectCenterVector } from './helpers'

interface CameraSettings {
  fov?: number
  near?: number
  far?: number
  // zoom?: number
}

export interface FrameViewerCamera {
  fitRatio: number
  fitPerRad: number
  rotationInitial: number
  rotationHover: number
  settings: CameraSettings
}

const defaultCameraSettings: Required<CameraSettings> = {
  fov: 75,
  near: 0.1,
  far: 1000,
}

export const defaultCamera: FrameViewerCamera = {
  fitRatio: 1.3,
  fitPerRad: 0.01,
  rotationInitial: 0,
  rotationHover: -45,
  settings: {
    fov: 30,
  },
}

/**
 * Creates a new Three.js directional camera.
 */
export const createCamera = (
  aspectRatio: number,
  settings?: CameraSettings,
) => {
  const finalSettings = Object.assign({}, defaultCameraSettings, settings ?? {})
  const camera = new THREE.PerspectiveCamera(
    finalSettings.fov,
    aspectRatio,
    finalSettings.near,
    finalSettings.far,
  )
  // camera.position.set(0, 0, finalSettings.zoom)

  return camera
}

/**
 * Fits camera to the bounding box of the object based on a ratio and optionally rotates around the object
 * @param camera Camera to be fitted against the object
 * @param object The object to fit the camera against
 * @param fitRatio How close the fit should be, larger value is further away from object
 * @param rotation The rotation (in radians) to apply to the camera [will orbit around object]
 * @param fitPerRad How much the rotation should add to the fit ratio per radian
 */
export const fitCameraToObject = (
  camera: THREE.PerspectiveCamera,
  object: THREE.Object3D,
  maxDimension: number,
  fitRatio: number,
  rotation: number,
  fitPerRad: number,
) => {
  const center = getObjectCenterVector(object)

  const fitHeightDistance =
    maxDimension / (2 * Math.atan((Math.PI * camera.fov) / 360))
  const fitWidthDistance = fitHeightDistance / camera.aspect
  const distance =
    (fitRatio + rotation * fitPerRad) *
    Math.max(fitHeightDistance, fitWidthDistance)

  const direction = new THREE.Vector3(0, 0, -1)
    .applyAxisAngle(object.up, -rotation)
    .multiplyScalar(distance)

  camera.near = distance / 100
  camera.far = distance * 100
  camera.updateProjectionMatrix()

  camera.position.copy(center).sub(direction)
  camera.lookAt(object.position)
}
